import * as React from 'react'
import styled, { css } from 'styled-components'
import CloudinaryImage from 'web-makeup/dist/CloudinaryImage'
import { FormattedRelative, FormattedMessage } from 'react-intl'
import Markdown from 'react-markdown'

import { CommentType } from '../../typings/types'

const Icon = require('antd/lib/icon')

export interface ChatBubbleProps {
  uuid: string
  avatar: {
    id: string
    type: string
  }
  type: CommentType
  name?: string | null
  message?: string | null
  className?: string
  onDelete?: (uuid: string) => void
  createdAt?: string | null
}

interface ContainerProps {
  loading: boolean
  type: CommentType
}

const Container = styled.div`
  position: relative;
  background-color: white;
  box-shadow: 0 2px 8px 0 rgba(0, 0, 0, 0.2);
  border-radius: 4px;
  font-size: 0.875rem;
  display: grid;
  padding: 1rem;
  grid-gap: 1rem;
  grid-template-columns: auto auto 1fr;
  grid-template-areas:
    'avatar name timestamp'
    'avatar message message';

  ${(props: ContainerProps) =>
    props.loading
      ? css`
          opacity: 0.6;
        `
      : ''};

  ${(props: ContainerProps) =>
    props.type === CommentType.NORMAL || props.type === CommentType.COMMENT
      ? ''
      : css`
          background: #f0f8ff;
        `};
`

const Avatar = styled(CloudinaryImage)`
  grid-area: avatar;
`

const Name = styled.span`
  font-weight: 700;
  color: #4a4a4a;
  grid-area: name;
`

const Message = styled.div`
  color: #757575;
  grid-area: message;

  ul,
  ol {
    padding-inline-start: 2rem;
    margin-block-start: 0.5rem;
    margin-block-end: 0.5rem;
  }

  li {
    display: list-item;
  }

  a {
    color: #06c;
    &:hover {
      text-decoration: underline;
    }
  }
`

const Timestamp = styled.span`
  grid-area: timestamp;
`

const Delete = styled.button`
  position: absolute;
  top: 1rem;
  right: 1.5rem;
  border: none;
  background-color: transparent;
  color: #757575;
  transition: color 0.2s ease-in-out;

  &:hover {
    color: #f5222d;
  }
`

const ReleaseNote = styled.div`
  position: absolute;
  bottom: -1.5rem;
  left: 0;
  color: #757575;
  line-height: 1.5rem;
  font-size: 0.75rem;
`

const ChatBubble = (props: ChatBubbleProps) => (
  <Container type={props.type} loading={props.uuid === '0-loading'} className={props.className}>
    <Avatar
      data-qa="chat-bubble__avatar"
      source={props.avatar}
      rounded
      sizes={{
        mobile: {
          width: 32,
          height: 32,
        },
      }}
    />

    {props.type === CommentType.RELEASE && (
      <ReleaseNote>
        <FormattedMessage id="chatBubble.release_note" />
      </ReleaseNote>
    )}

    {props.type === CommentType.RESOLVE && (
      <ReleaseNote>
        <FormattedMessage id="chatBubble.resolve_note" />
      </ReleaseNote>
    )}

    {props.type === CommentType.MODERATION_REJECT && (
      <ReleaseNote>
        <FormattedMessage id="chatBubble.moderation_note" />
      </ReleaseNote>
    )}

    <Name data-qa="chat-bubble__name">{props.name}</Name>
    {props.createdAt && (
      <Timestamp>
        <FormattedRelative value={props.createdAt} />
      </Timestamp>
    )}

    <Message data-qa="chat-bubble__message">
      <Markdown source={props.message || ''} escapeHtml={true} linkTarget="_blank" />
    </Message>

    {props.onDelete && (
      <Delete
        data-qa="chat-bubble__delete"
        onClick={() => props.onDelete && props.onDelete(props.uuid)}
      >
        <Icon type="delete" />
      </Delete>
    )}
  </Container>
)

export default ChatBubble



// WEBPACK FOOTER //
// ./src/components/ChatBubble/ChatBubble.tsx